/**
 * Course certificates — the editor.
 *
 * This page used to be read-only, on the grounds that activation ran through Studio's
 * `@login_required` handler and could not be reached from a tenant origin. That was true of
 * the URL and not of the operation: the writes are now wrapped in our own authoring app
 * (`shared/lib/certificates.ts` says why), so this is an editor.
 *
 * THREE THINGS DECIDE WHETHER A LEARNER EVER SEES A CERTIFICATE, and only one of them is
 * obvious. The page says all three out loud, because every one of them fails silently:
 *
 *   1. A certificate must exist and be ACTIVE. Without an active configuration the
 *      platform's certificate page 404s and the learner's certificate list omits the
 *      course entirely — `has_any_active_web_certificate` is checked on both paths.
 *   2. The course must offer an enrolment mode that can carry one. Audit cannot; honor,
 *      verified and professional can. This install defaults every course to honor.
 *   3. The course must SHOW certificates before it ends. `certificates_display_behavior`
 *      defaults to "end", which resolves against the course end date — so a course with no
 *      end date issues certificates and shows them to nobody, permanently. That is the
 *      checkbox in the form, not a footnote.
 *
 * ONE CERTIFICATE PER COURSE. The platform's field is a list and every consumer takes the
 * first entry, so a second one is storage that nothing renders. The form edits one.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Icon,
  Kpi,
  KpiRow,
  Spinner,
} from '../../shared/ui';
import { PageHeader } from '../AdminShell';
import { getConfig } from '../../shared/lib/config';
import { fractionPercent, getCourseVerdict } from '../../shared/lib/answers';
import {
  deleteCourseCertificate,
  explainCertificateFailure,
  saveCourseCertificate,
  setCourseCertificateActive,
  type CertificateSignatoryInput,
} from '../../shared/lib/certificates';
import {
  explainFailure,
  getCourseCertificates,
  resolveCourseKey,
  type CourseCertificate,
} from '../../shared/lib/studio-settings';
import './course-settings.css';

/**
 * The platform builds this URL by concatenation and can emit "///certificates/…" when it
 * has no configured LMS host. Three slashes is a scheme-relative URL with an empty
 * authority, which some browsers refuse; collapse it to a plain path. A genuine
 * protocol-relative "//host/…" is left alone.
 */
function safePreviewUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('///') ? url.slice(2) : url;
}

/** A signatory row while it is being edited. `key` is React's, never sent. */
interface SignatoryDraft extends CertificateSignatoryInput {
  key: string;
}

interface Draft {
  name: string;
  description: string;
  course_title: string;
  show_as_soon_as_earned: boolean;
  signatories: SignatoryDraft[];
}

let rowCounter = 0;
const newRow = (): SignatoryDraft => ({
  key: `row-${(rowCounter += 1)}`,
  name: '',
  title: '',
  organization: '',
  signature_image_path: '',
});

function draftFrom(certificate: CourseCertificate | undefined, courseTitle: string, showEarly: boolean): Draft {
  if (!certificate) {
    return {
      // A course with no certificate yet gets sensible text rather than an empty form:
      // the platform requires both a name and a description, and refusing to save an
      // empty form teaches nothing that pre-filling it does not.
      name: 'Certificate of completion',
      description: `Awarded on completing ${courseTitle}.`,
      course_title: '',
      show_as_soon_as_earned: showEarly,
      signatories: [newRow()],
    };
  }
  return {
    name: certificate.name ?? '',
    description: certificate.description ?? '',
    course_title: certificate.course_title ?? '',
    show_as_soon_as_earned: showEarly,
    signatories: (certificate.signatories ?? []).map((signatory) => ({
      key: `row-${(rowCounter += 1)}`,
      id: typeof signatory.id === 'number' ? signatory.id : undefined,
      name: signatory.name ?? '',
      title: signatory.title ?? '',
      organization: signatory.organization ?? '',
      signature_image_path: signatory.signature_image_path ?? '',
    })),
  };
}

export default function CourseCertificates() {
  const params = useParams();
  const [search] = useSearchParams();
  const courseKey = resolveCourseKey(params, search);
  const config = getConfig();
  const slug = config.tenant.slug;
  const queryClient = useQueryClient();

  const queryKey = ['course-certificates', courseKey];
  const certificates = useQuery({
    queryKey,
    queryFn: () => getCourseCertificates(courseKey as string),
    enabled: Boolean(courseKey),
  });

  /**
   * Whether the grading policy behind this certificate can pass anyone at all — see
   * `shared/lib/answers.ts`. A certificate can be perfectly configured (name, description,
   * signatories, switched on) and still never reach a learner, because certification and
   * grading are two different subsystems that this page's own form cannot see across. Own
   * query rather than folded into `certificates` above: this is a fact about the COURSE, not
   * about the certificate record, and it needs to stay visible even while the form is dirty.
   */
  const verdict = useQuery({
    queryKey: ['course-verdict', courseKey],
    queryFn: () => getCourseVerdict(courseKey as string),
    enabled: Boolean(courseKey),
  });

  const context = certificates.data;
  const stored = context?.certificates?.[0];
  const courseTitle = context?.course_title ?? 'this course';

  /**
   * Whether the course currently shows certificates before it ends.
   *
   * NOT AVAILABLE ON THIS ENDPOINT. The contentstore certificates context reports the
   * certificate and the modes, not `certificates_display_behavior` — that lives on the
   * schedule-and-details endpoint. Rather than a second round trip on every load, the form
   * defaults it to on (which is the behaviour a new course is created with here) and sends
   * it explicitly on save. So saving asserts the answer instead of guessing it, and the
   * text beside the checkbox says what saving will do.
   */
  const [draft, setDraft] = useState<Draft | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Reset the form whenever the server's version changes — after a save, an activation, or
  // a refetch. Keyed on the stored id and its content so a background refetch that returns
  // the same certificate does not wipe what is being typed.
  const identity = useMemo(
    () => (stored ? JSON.stringify([stored.id, stored.name, stored.description, stored.course_title, stored.signatories]) : 'none'),
    [stored],
  );
  useEffect(() => {
    if (!context) return;
    setDraft(draftFrom(stored, courseTitle, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, Boolean(context)]);

  const save = useMutation({
    mutationFn: (activate: boolean) => {
      const current = draft as Draft;
      return saveCourseCertificate(slug, courseKey as string, {
        name: current.name.trim(),
        description: current.description.trim(),
        course_title: current.course_title.trim(),
        is_active: activate,
        show_as_soon_as_earned: current.show_as_soon_as_earned,
        signatories: current.signatories
          // A blank row is how the form offers "add one", so an untouched one is not an
          // error — it is simply not a signatory.
          .filter((row) => row.name.trim() || row.title.trim() || row.organization.trim())
          .map(({ key, ...rest }) => ({
            ...rest,
            name: rest.name.trim(),
            title: rest.title.trim(),
            organization: rest.organization.trim(),
          })),
      });
    },
    onSuccess: () => {
      setFailure(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      setSaved(false);
      setFailure(explainCertificateFailure(error));
    },
  });

  const activate = useMutation({
    mutationFn: (isActive: boolean) => setCourseCertificateActive(slug, courseKey as string, isActive),
    onSuccess: () => {
      setFailure(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => setFailure(explainCertificateFailure(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteCourseCertificate(slug, courseKey as string),
    onSuccess: () => {
      setFailure(null);
      setSaved(false);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => setFailure(explainCertificateFailure(error)),
  });

  if (!courseKey) {
    return (
      <>
        <PageHeader title="Certificates" />
        <Banner tone="warning">
          <strong>No course selected.</strong> Open this page from a course, or add the
          course ID to the address as <code>?course=course-v1:ORG+NUMBER+RUN</code>.
        </Banner>
      </>
    );
  }

  if (certificates.isLoading) {
    return (
      <>
        <PageHeader title="Certificates" />
        <Spinner label="Loading certificate configuration…" />
      </>
    );
  }

  if (certificates.isError) {
    return (
      <>
        <PageHeader title="Certificates" />
        <Banner tone="danger">
          <strong>Could not load this course&rsquo;s certificates.</strong>{' '}
          {explainFailure(certificates.error)}{' '}
          <Button variant="ghost" onClick={() => certificates.refetch()}>
            Try again
          </Button>
        </Banner>
      </>
    );
  }

  if (!context || !draft) {
    return (
      <>
        <PageHeader title="Certificates" />
        <Spinner label="Loading certificate configuration…" />
      </>
    );
  }

  const preview = safePreviewUrl(context.certificate_web_view_url);
  const isActive = context.is_active;
  const busy = save.isPending || activate.isPending || remove.isPending;
  const set = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((current) => (current ? { ...current, [field]: value } : current));

  return (
    <>
      <PageHeader
        title="Certificates"
        description={`${context.course_title} · ${context.course_number_override || context.course_number}`}
      />
      {/*
        NO "Open in Studio" ACTION HERE ANY MORE, for two reasons that arrived together.
        It pointed at Studio's OWN hostname, and sessions are now scoped per host — so following
        it asked the administrator to sign in a second time, which is the isolation working, not a
        fault to route around. And it led to a second editor for the thing this page already
        edits: the form below is the certificate's title, description and signatories, saved
        through our own endpoint. Two ways to edit one object is how they end up disagreeing.
      */}

      {failure && (
        <Banner tone="danger">
          <strong>That did not save.</strong> {failure}
        </Banner>
      )}

      {/* Without a certificate-bearing enrolment mode nothing can be awarded, whatever is
          configured here — so this is stated before the form rather than discovered after
          it. Every course on this install defaults to honor, so seeing this means the
          course defines its own modes and they are all audit. */}
      {!context.has_certificate_modes ? (
        <Card title="Certificates are not available for this course">
          <EmptyState
            title="No enrolment mode can carry a certificate"
            body={
              context.course_modes.length === 0
                ? 'This course has no enrolment modes at all, so nothing can award a certificate. A platform administrator needs to add a certificate-bearing mode — honor, verified or professional — to the course.'
                : `This course only offers ${context.course_modes.join(', ')}. Audit-only enrolment cannot award a certificate. A platform administrator needs to add a certificate-bearing mode — honor, verified or professional — before certificates can be configured.`
            }
          />
        </Card>
      ) : (
        <>
          {/*
            THREE THINGS decide whether a learner ever sees a certificate — see the file
            header. This is the FOURTH, upstream of all of them: whether the grading policy
            can pass anyone at all. A misconfigured course must not read like "not set up
            yet" — that invites setting the certificate up again, which changes nothing,
            because the fault is on the Grading tab, not here.
          */}
          {verdict.data &&
            (verdict.data.grading === 'misconfigured' ? (
              <Banner tone="danger">
                <strong>This course cannot currently issue certificates.</strong> Its grading
                policy cannot reach the pass mark: the highest score any learner can achieve
                is {fractionPercent(verdict.data.max_achievable_percent) ?? '0'}%, below the{' '}
                {fractionPercent(verdict.data.pass_cutoff) ?? '?'}% pass mark. Nobody can pass,
                so no certificate will be issued here however this form is filled in — fix the
                grading policy on this course&rsquo;s{' '}
                <code>Grading</code> tab first.
              </Banner>
            ) : (
              <Banner tone="info">
                <strong>Certificate rule:</strong> {verdict.data.certificate_rule}
              </Banner>
            ))}

          <KpiRow>
            <Kpi
              label="Certificate"
              value={stored ? 'Configured' : 'Not set up'}
              hint={stored ? stored.name : 'Fill in the form below and save'}
            />
            <Kpi
              label="Switched on"
              value={isActive ? 'Yes' : 'No'}
              hint={isActive ? 'Learners who qualify receive it' : 'Learners receive nothing yet'}
            />
            <Kpi label="Signatories" value={stored?.signatories.length ?? 0} />
          </KpiRow>

          <Banner tone={isActive ? 'success' : 'info'}>
            <strong>
              {isActive
                ? 'Certificates are switched on for this course.'
                : stored
                  ? 'This certificate is configured but not switched on.'
                  : 'This course has no certificate yet.'}
            </strong>{' '}
            {isActive
              ? 'Learners who complete the course, or pass it where it is graded, can see and download theirs.'
              : 'Until it is switched on, the platform will not show a certificate to anybody — even one that has already been earned.'}
          </Banner>

          <Card
            title={stored ? 'Certificate' : 'Set up the certificate'}
            action={
              preview && (
                <a className="ui-btn" data-variant="ghost" href={preview}>
                  Preview
                </a>
              )
            }
          >
            <div className="cs-form">
              <div className="cs-grid">
                <Field
                  label="Name"
                  name="certificate-name"
                  value={draft.name}
                  onChange={(event) => set('name', event.target.value)}
                  hint="Printed as the heading. Required."
                />
                <Field
                  label="Course title on the certificate"
                  name="certificate-course-title"
                  value={draft.course_title}
                  onChange={(event) => set('course_title', event.target.value)}
                  hint={`Leave blank to use “${context.course_title}”.`}
                />
              </div>

              <div className="cs-area">
                <label htmlFor="certificate-description">Description</label>
                <textarea
                  id="certificate-description"
                  rows={3}
                  value={draft.description}
                  onChange={(event) => set('description', event.target.value)}
                />
                <p className="cs-note">
                  Required by the platform. It is not printed on the certificate — it
                  describes the certificate to whoever configures it next.
                </p>
              </div>

              <label className="cs-check">
                <input
                  type="checkbox"
                  checked={draft.show_as_soon_as_earned}
                  onChange={(event) => set('show_as_soon_as_earned', event.target.checked)}
                />
                <span>
                  Show each certificate as soon as it is earned
                  <small>
                    Leave this on unless the academy deliberately holds certificates back
                    until a course ends. With it off, and no end date set on the course, an
                    earned certificate is never shown to anybody.
                  </small>
                </span>
              </label>

              <h3 className="cs-legend">Signatories</h3>
              <p className="cs-note">
                Each one prints a signature line. A certificate with none is valid and
                prints without signatures. Signature images are uploaded to the course&rsquo;s
                files in Studio; paste the asset path here.
              </p>

              {draft.signatories.map((row, index) => (
                <div className="cs-sigrow" key={row.key}>
                  <div className="cs-grid">
                    <Field
                      label="Name"
                      name={`signatory-name-${index}`}
                      value={row.name}
                      onChange={(event) => updateRow(setDraft, index, { name: event.target.value })}
                    />
                    <Field
                      label="Role or title"
                      name={`signatory-title-${index}`}
                      value={row.title}
                      onChange={(event) => updateRow(setDraft, index, { title: event.target.value })}
                    />
                    <Field
                      label="Organisation"
                      name={`signatory-org-${index}`}
                      value={row.organization}
                      onChange={(event) =>
                        updateRow(setDraft, index, { organization: event.target.value })
                      }
                    />
                    <Field
                      label="Signature image path"
                      name={`signatory-image-${index}`}
                      value={row.signature_image_path}
                      onChange={(event) =>
                        updateRow(setDraft, index, { signature_image_path: event.target.value })
                      }
                      hint="Optional. Looks like /asset-v1:ORG+NUM+RUN+type@asset+block@signature.png"
                    />
                  </div>
                  <div className="cs-sigrow__foot">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setDraft((current) =>
                          current
                            ? {
                                ...current,
                                signatories: current.signatories.filter((_, i) => i !== index),
                              }
                            : current,
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}

              <div className="cs-inline">
                <Button
                  variant="ghost"
                  onClick={() =>
                    setDraft((current) =>
                      current ? { ...current, signatories: [...current.signatories, newRow()] } : current,
                    )
                  }
                >
                  <Icon name="add" />
                  Add a signatory
                </Button>
              </div>

              <div className="cs-bar">
                <span className="cs-bar__state">
                  {saved && !busy ? 'Saved.' : stored ? `Version ${stored.version}` : 'Not saved yet'}
                </span>
                <div className="cs-bar__end">
                  {stored && (
                    <Button
                      variant="ghost"
                      busy={remove.isPending}
                      disabled={busy}
                      onClick={() => remove.mutate()}
                    >
                      Delete
                    </Button>
                  )}
                  {stored && (
                    <Button
                      variant="secondary"
                      busy={activate.isPending}
                      disabled={busy}
                      onClick={() => activate.mutate(!isActive)}
                    >
                      {isActive ? 'Switch off' : 'Switch on'}
                    </Button>
                  )}
                  {/* Saving keeps the current on/off state. A course with no certificate
                      yet gets one switched ON, because a configuration nobody switches on
                      is the single most common way this feature silently does nothing. */}
                  <Button
                    variant="primary"
                    busy={save.isPending}
                    disabled={busy || !draft.name.trim() || !draft.description.trim()}
                    onClick={() => save.mutate(stored ? isActive : true)}
                  >
                    {stored ? 'Save changes' : 'Create and switch on'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {stored && <StoredCertificate certificate={stored} isActive={isActive} />}

          <Card title="Enrolment modes">
            <div className="cs-inline">
              {context.course_modes.length === 0 ? (
                <p className="cs-note">No enrolment modes are configured.</p>
              ) : (
                context.course_modes.map((mode) => (
                  <Chip key={mode} icon="badge" tone="brand">
                    {mode}
                  </Chip>
                ))
              )}
            </div>
            <p className="cs-note">
              A learner receives a certificate only if their enrolment mode can carry one.
              Audit cannot; honor, verified and professional can. Courses on this
              installation default to honor, so no per-course change is needed.
            </p>
          </Card>
        </>
      )}
    </>
  );
}

function updateRow(
  setDraft: React.Dispatch<React.SetStateAction<Draft | null>>,
  index: number,
  patch: Partial<SignatoryDraft>,
) {
  setDraft((current) =>
    current
      ? {
          ...current,
          signatories: current.signatories.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        }
      : current,
  );
}

/**
 * What the server actually holds, shown beside the form.
 *
 * Not decoration. The form is a draft and this is the record, and the two differing is the
 * normal state of an editor — an admin who has typed but not saved needs to be able to see
 * which is which without reloading the page.
 */
function StoredCertificate({
  certificate,
  isActive,
}: {
  certificate: CourseCertificate;
  isActive: boolean;
}) {
  return (
    <Card title="Stored on the course">
      <div className="cs-cert">
        <div className="cs-cert__head">
          <h3>{certificate.name || 'Untitled certificate'}</h3>
          {isActive ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Not active</Badge>}
          <Badge>Version {certificate.version}</Badge>
        </div>

        {certificate.description && <p className="cs-cert__desc">{certificate.description}</p>}

        {certificate.course_title && (
          <dl className="cs-facts">
            <div>
              <dt>Course title on the certificate</dt>
              <dd>{certificate.course_title}</dd>
            </div>
          </dl>
        )}

        {certificate.signatories.length === 0 ? (
          <p className="cs-note">
            No signatories. The certificate will print without a signature line.
          </p>
        ) : (
          <ul className="cs-sigs">
            {certificate.signatories.map((signatory) => (
              <li className="cs-sig" key={signatory.id}>
                <strong>{signatory.name}</strong>
                <span>{signatory.title}</span>
                {signatory.organization && <span>{signatory.organization}</span>}
                <SignatureImage path={signatory.signature_image_path} name={signatory.name} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/**
 * Signature image, with the asset path as the fallback.
 *
 * Signature images live on Studio's asset path, which the tenant origin does not proxy in
 * every environment. Falling back to the path keeps the row informative instead of
 * leaving a broken-image icon that looks like the signature was lost.
 */
function SignatureImage({ path, name }: { path: string; name: string }) {
  const [failed, setFailed] = useState(false);

  if (!path) return <span>No signature image</span>;
  if (failed) return <span className="mono">{path.split('@').pop() ?? path}</span>;

  return <img src={path} alt={`Signature of ${name}`} onError={() => setFailed(true)} />;
}
